// imperial.ts — Parse & format imperial dimensions (ANSI Y14.5 architectural)
//
// Internal unit is always decimal inches.
// ANSI display: 3'-1 1/2"  (feet mark, dash, inches space fraction, inch mark)
// Accepts input: 37.5 | 3' 1 1/2" | 3'1-1/2" | 3' 1.5" | 3' | 6" | 1/2" etc.

const IN_TO_MM = 25.4;

// ── Fraction tables ──────────────────────────────────────────────────────────

const FRAC_TABLE: [number, string][] = [
  [0, ''],
  [1, '1/16'],
  [2, '1/8'],
  [3, '3/16'],
  [4, '1/4'],
  [5, '5/16'],
  [6, '3/8'],
  [7, '7/16'],
  [8, '1/2'],
  [9, '9/16'],
  [10, '5/8'],
  [11, '11/16'],
  [12, '3/4'],
  [13, '13/16'],
  [14, '7/8'],
  [15, '15/16'],
];

/** Map fraction string → decimal value (for parsing) */
const FRAC_TO_DEC: Record<string, number> = {
  '1/16': 1 / 16,
  '1/8': 1 / 8, '2/16': 1 / 8,
  '3/16': 3 / 16,
  '1/4': 1 / 4, '2/8': 1 / 4, '4/16': 1 / 4,
  '5/16': 5 / 16,
  '3/8': 3 / 8, '6/16': 3 / 8,
  '7/16': 7 / 16,
  '1/2': 1 / 2, '2/4': 1 / 2, '4/8': 1 / 2, '8/16': 1 / 2,
  '9/16': 9 / 16,
  '5/8': 5 / 8, '10/16': 5 / 8,
  '11/16': 11 / 16,
  '3/4': 3 / 4, '6/8': 3 / 4, '12/16': 3 / 4,
  '13/16': 13 / 16,
  '7/8': 7 / 8, '14/16': 7 / 8,
  '15/16': 15 / 16,
};

// ── Parse ────────────────────────────────────────────────────────────────────

/**
 * Parse an imperial dimension string into decimal inches.
 *
 * Accepted formats (all return inches):
 *   37.5              plain decimal inches
 *   37.5"             with inch mark
 *   3'                feet only (→ 36)
 *   3' 1 1/2"         ft-in-frac (→ 37.5)
 *   3'-1 1/2"         ft-dash-in-frac (→ 37.5)
 *   3'1-1/2"          ft-in-hyphen-frac (→ 37.5)
 *   3' 1.5"           ft-in-decimal (→ 37.5)
 *   1 1/2"            in-frac only (→ 1.5)
 *   1/2"              fraction only (→ 0.5)
 *   320 mm            metric (→ converts to inches)
 *
 * Returns null if unparseable.
 */
export function parseImperial(s: string): number | null {
  let t = s.trim();
  if (!t) return null;

  // Metric: "320 mm" or "320mm"
  const mmMatch = t.match(/^(-?\d+(?:\.\d+)?)\s*mm$/i);
  if (mmMatch) return parseFloat(mmMatch[1]) / IN_TO_MM;

  // Strip trailing inch mark for simplicity
  t = t.replace(/"$/, '').trim();

  // ── Feet + inches patterns ──────────────────────────────────────────────

  // ft' in frac  e.g. "3' 1 1/2" or "3'-1 1/2" or "3' 1-1/2"
  const ftInFrac = t.match(/^(-?\d+(?:\.\d+)?)'\s*-?\s*(\d+)\s+(\d+\/\d+)$/);
  if (ftInFrac) {
    const ft = parseFloat(ftInFrac[1]);
    const inch = parseFloat(ftInFrac[2]);
    const frac = FRAC_TO_DEC[ftInFrac[3]];
    if (frac !== undefined) return ft * 12 + inch + frac;
  }

  // ft' in-frac  e.g. "3'1-1/2" (no space before inches, hyphen before frac)
  const ftInHyphenFrac = t.match(/^(-?\d+(?:\.\d+)?)'\s*(\d+)-(\d+\/\d+)$/);
  if (ftInHyphenFrac) {
    const ft = parseFloat(ftInHyphenFrac[1]);
    const inch = parseFloat(ftInHyphenFrac[2]);
    const frac = FRAC_TO_DEC[ftInHyphenFrac[3]];
    if (frac !== undefined) return ft * 12 + inch + frac;
  }

  // ft in frac (no foot mark)  e.g. "10 2 3/4" → 10 feet 2¾ inches
  const ftInFracNoMark = t.match(/^(-?\d+)\s+(\d+)\s+(\d+\/\d+)$/);
  if (ftInFracNoMark) {
    const ft = parseFloat(ftInFracNoMark[1]);
    const inch = parseFloat(ftInFracNoMark[2]);
    const frac = FRAC_TO_DEC[ftInFracNoMark[3]];
    if (frac !== undefined) return ft * 12 + inch + frac;
  }

  // ft' in  e.g. "3' 6" or "3'-6" or "3'6"
  const ftIn = t.match(/^(-?\d+(?:\.\d+)?)'\s*-?\s*(\d+(?:\.\d+)?)$/);
  if (ftIn) return parseFloat(ftIn[1]) * 12 + parseFloat(ftIn[2]);

  // ft' frac  e.g. "3' 1/2"
  const ftFrac = t.match(/^(-?\d+(?:\.\d+)?)'\s*-?\s*(\d+\/\d+)$/);
  if (ftFrac) {
    const frac = FRAC_TO_DEC[ftFrac[2]];
    if (frac !== undefined) return parseFloat(ftFrac[1]) * 12 + frac;
  }

  // ft' only  e.g. "3'"
  const ftOnly = t.match(/^(-?\d+(?:\.\d+)?)'$/);
  if (ftOnly) return parseFloat(ftOnly[1]) * 12;

  // ── Inches-only patterns ────────────────────────────────────────────────

  // in frac  e.g. "1 1/2"
  const inFrac = t.match(/^(-?\d+)\s+(\d+\/\d+)$/);
  if (inFrac) {
    const frac = FRAC_TO_DEC[inFrac[2]];
    if (frac !== undefined) return parseFloat(inFrac[1]) + frac;
  }

  // frac only  e.g. "1/2"
  const fracOnly = t.match(/^(\d+\/\d+)$/);
  if (fracOnly) {
    const frac = FRAC_TO_DEC[fracOnly[1]];
    if (frac !== undefined) return frac;
  }

  // Plain decimal  e.g. "37.5" or "-6"
  const dec = t.match(/^-?\d+(?:\.\d+)?$/);
  if (dec) return parseFloat(t);

  return null;
}

// ── Format ───────────────────────────────────────────────────────────────────

/**
 * Format decimal inches to ANSI architectural string.
 *
 * Uses 1/16" resolution. Examples:
 *   37.5   → "3'-1 1/2""
 *   36     → "3'-0""
 *   6      → "6""
 *   0.5    → "1/2""
 *   0      → "0""
 */
export function formatImperial(totalInches: number, unit = '"'): string {
  const neg = totalInches < 0;
  const abs = Math.abs(totalInches);
  const sixteenths = Math.round(abs * 16);
  const ft = Math.floor(sixteenths / (12 * 16));
  const remSixteenths = sixteenths % (12 * 16);
  const wholeInch = Math.floor(remSixteenths / 16);
  const fracIdx = remSixteenths % 16;
  const frac = FRAC_TABLE[fracIdx]?.[1] ?? '';
  const sign = neg ? '-' : '';

  if (ft === 0) {
    // Inches only
    if (frac) {
      return wholeInch > 0
        ? `${sign}${wholeInch} ${frac}${unit}`
        : `${sign}${frac}${unit}`;
    }
    return `${sign}${wholeInch}${unit}`;
  }

  // Feet + inches
  if (frac) {
    return wholeInch > 0
      ? `${sign}${ft}'-${wholeInch} ${frac}${unit}`
      : `${sign}${ft}'-${frac}${unit}`;
  }
  return `${sign}${ft}'-${wholeInch}${unit}`;
}

/**
 * Format decimal inches to metric string (mm).
 */
export function formatMetric(totalInches: number): string {
  return `${Math.round(totalInches * IN_TO_MM)} mm`;
}
